/**
 * Utility functions for donation validation
 * Gets minimum donation amounts from CMS data
 */

/**
 * Get minimum donation amount from CMS
 * @param {Object} cmsHome - CMS home data from Redux state
 * @param {boolean} isOverseas - Whether this is for overseas donation
 * @returns {number} Minimum donation amount (default: 300 for regular, 10 for overseas)
 */
export const getMinimumDonationAmount = (cmsHome, isOverseas = false) => {
  // Default values
  const defaultRegularMin = 300;
  const defaultOverseasMin = 10;

  // If CMS data is not available, return defaults
  if (!cmsHome || !cmsHome.data || !Array.isArray(cmsHome.data)) {
    return isOverseas ? defaultOverseasMin : defaultRegularMin;
  }

  // Find the CMS section for "Payment Page" and "Minimum Donation Amount"
  const minDonationSection = cmsHome.data.find(
    (section) =>
      section?.page === "Payment Page" &&
      section?.section === "Minimum Donation Amount" &&
      (section?.status === 1 || section?.status === true)
  );

  if (!minDonationSection) {
    return isOverseas ? defaultOverseasMin : defaultRegularMin;
  }

  // For overseas donations, use section_title (default: "10")
  if (isOverseas) {
    const overseasMin = Number(minDonationSection.section_title) || defaultOverseasMin;
    return overseasMin;
  }

  // For regular donations, use title (default: "300")
  const regularMin = Number(minDonationSection.title) || defaultRegularMin;
  return regularMin;
};

/**
 * Strip anything that is not a digit or a single decimal point.
 *
 * Amount inputs used to accept whatever the browser gave them, so a pasted
 * "5e80" became a real JavaScript number and travelled to the payment API.
 * Rejecting the characters at the input is the simplest place to stop that,
 * and mirrors what SafeLifeEveryMonth already does.
 *
 * Purely additive: it removes characters that could never form a valid amount,
 * and does not change any minimum, maximum or rounding behaviour.
 *
 * @param {string} raw - Raw input value
 * @returns {string} Sanitised value safe to pass to Number()
 */
export const sanitizeAmountInput = (raw) => {
  const digitsAndDots = String(raw ?? "").replace(/[^\d.]/g, "");
  const [whole, ...rest] = digitsAndDots.split(".");
  return rest.length > 0 ? `${whole}.${rest.join("")}` : whole;
};

/**
 * Validate donation amount
 * @param {number|string} amount - The donation amount to validate
 * @param {Object} cmsHome - CMS home data from Redux state
 * @param {boolean} isOverseas - Whether this is for overseas donation
 * @param {string} currencySymbol - Currency symbol to display in error message (e.g., "₹", "$", "£")
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validateDonationAmount = (amount, cmsHome, isOverseas = false, currencySymbol = "₹") => {
  const numAmount = Number(amount);
  const minAmount = getMinimumDonationAmount(cmsHome, isOverseas);

  // Number.isFinite rather than !isNaN: isNaN(Infinity) is false, so a long
  // enough run of digits overflows to Infinity and used to pass this check.
  // Same rejection message and same minimum handling below — this only closes
  // the Infinity case, which no gateway could ever charge.
  if (!amount || !Number.isFinite(numAmount) || numAmount <= 0) {
    return {
      isValid: false,
      error: "Please enter a valid donation amount",
    };
  }

  if (numAmount < minAmount) {
    return {
      isValid: false,
      error: `Minimum donation amount is ${currencySymbol}${minAmount}`,
    };
  }

  return {
    isValid: true,
    error: null,
  };
};

